import { authHeader, type ParsedDsn } from './dsn';
import type { OutpostEvent } from './types';

export interface TransportOptions {
  dsn: ParsedDsn;
  clientName: string;
  version: string;
  timeout?: number;
  debug?: boolean;
}

export interface SendResult {
  ok: boolean;
  status?: number;
  /** Seconds the server asked us to wait, from `Retry-After`. */
  retryAfter?: number;
}

/** Attempts before an event is dropped. */
const MAX_ATTEMPTS = 3;

/** Events buffered while offline or rate limited. */
const MAX_QUEUE = 30;

/**
 * Delivers events, with retry and an offline buffer.
 *
 * The guiding rule is that reporting must never harm the host application: a
 * failed send is retried a few times with backoff, then dropped. An error
 * monitor that blocks a page or retries forever is worse than one that loses
 * the occasional report.
 */
export class Transport {
  private queue: OutpostEvent[] = [];
  private flushing = false;
  /** Epoch millis before which we must not send, set by a 429. */
  private blockedUntil = 0;

  constructor(private options: TransportOptions) {}

  /** Send an event, queueing it if delivery is not currently possible. */
  async send(event: OutpostEvent): Promise<SendResult> {
    if (Date.now() < this.blockedUntil) {
      this.enqueue(event);
      return { ok: false, status: 429 };
    }

    const result = await this.attempt(event);

    if (!result.ok) {
      if (result.retryAfter) {
        this.blockedUntil = Date.now() + result.retryAfter * 1000;
      }
      // 4xx other than 429 means the payload will never be accepted; retrying
      // wastes the user's bandwidth.
      if (this.isRetryable(result.status)) {
        this.enqueue(event);
      }
    } else {
      void this.flush();
    }

    return result;
  }

  /**
   * Best-effort delivery during page unload.
   *
   * `sendBeacon` cannot set headers, so the key travels as a query parameter —
   * the ingest endpoint accepts both forms for exactly this reason.
   */
  sendBeacon(event: OutpostEvent): boolean {
    // Structurally typed rather than relying on the DOM lib: this file is shared
    // with @peaky-ai/outpost-node, which is compiled without it.
    const beacon = (
      globalThis as {
        navigator?: { sendBeacon?: (url: string, data?: Blob) => boolean };
      }
    ).navigator?.sendBeacon;

    if (typeof beacon !== 'function') return false;

    const url = `${this.options.dsn.eventUrl}?key=${encodeURIComponent(this.options.dsn.publicKey)}`;
    const blob = new Blob([JSON.stringify(event)], { type: 'application/json' });
    return beacon.call(globalThis.navigator, url, blob);
  }

  /** Deliver anything buffered, as one NDJSON batch. */
  async flush(): Promise<void> {
    if (this.flushing || this.queue.length === 0 || Date.now() < this.blockedUntil) return;

    this.flushing = true;
    const batch = this.queue.splice(0, this.queue.length);

    try {
      const response = await this.post(
        this.options.dsn.batchUrl,
        batch.map((event) => JSON.stringify(event)).join('\n'),
      );
      if (!response.ok && this.isRetryable(response.status)) {
        // Put them back, newest-first order preserved, still capped.
        this.queue = [...batch, ...this.queue].slice(-MAX_QUEUE);
      }
    } catch {
      this.queue = [...batch, ...this.queue].slice(-MAX_QUEUE);
    } finally {
      this.flushing = false;
    }
  }

  /** Number of events currently buffered. Exposed for tests and diagnostics. */
  pending(): number {
    return this.queue.length;
  }

  private enqueue(event: OutpostEvent): void {
    this.queue.push(event);
    // Drop the oldest: during an outage the most recent errors are the ones
    // still worth having.
    if (this.queue.length > MAX_QUEUE) {
      this.queue.splice(0, this.queue.length - MAX_QUEUE);
    }
  }

  private isRetryable(status?: number): boolean {
    if (status === undefined) return true; // network failure
    if (status === 429) return true;
    return status >= 500;
  }

  private async attempt(event: OutpostEvent): Promise<SendResult> {
    let lastResult: SendResult = { ok: false };

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      if (attempt > 0) {
        // Exponential backoff with jitter, so a recovering server is not hit by
        // every client in lockstep.
        const backoff = 2 ** attempt * 100 + Math.random() * 100;
        await new Promise((resolve) => setTimeout(resolve, backoff));
      }

      try {
        const response = await this.post(this.options.dsn.eventUrl, JSON.stringify(event));
        if (response.ok) return response;

        lastResult = response;
        if (!this.isRetryable(response.status)) return response;
      } catch (error) {
        this.log('send failed', error);
        lastResult = { ok: false };
      }
    }

    return lastResult;
  }

  private async post(url: string, body: string): Promise<SendResult> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.options.timeout ?? 5000);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Outpost-Auth': authHeader(
            this.options.dsn,
            this.options.clientName,
            this.options.version,
          ),
        },
        body,
        // Reporting must never delay a navigation or hold the page open.
        keepalive: true,
        signal: controller.signal,
      });

      const retryAfter = Number(response.headers.get('Retry-After'));
      return {
        ok: response.ok,
        status: response.status,
        retryAfter: Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined,
      };
    } finally {
      clearTimeout(timer);
    }
  }

  private log(...args: unknown[]): void {
    if (this.options.debug) {
      // eslint-disable-next-line no-console
      console.warn('[outpost]', ...args);
    }
  }
}
